import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "../ui/useToast";
import NotificationContent from "@/components/notification/notificationContent";
import { useMap } from "@/hooks/map/core/useMapContext";
import { FeatureCollection, GeoJsonProperties } from "geojson";
import { GeoJSONSource } from "maplibre-gl";
import {
  ExtendedColumnDef,
  SpatialAnalysisRow
} from "@/components/spatialAnalysis/spatialAnalysisTable/columns";
import { Row } from "@tanstack/react-table";
import { DataSchemaType } from "@/types/dataset";
import { useDataset } from "@/hooks/map/useDataset";

type TypeGeomPreview = "grid" | "wkt";

export type PreviewData = {
  value: string | null;
  type: "grid" | "wkt";
};

interface IUsePreviewGeom {
  handleClose: () => void;
  selectedDataset: string | null | undefined;
}

export const usePreviewGeom = ({
  handleClose,
  selectedDataset
}: IUsePreviewGeom) => {
  const { handleHighlightDatasetFeatureByWkt } = useDataset();
  const { showToast } = useToast();
  const { map } = useMap();

  const handlePreviewClick = async (
    value: string | null,
    type: "grid" | "wkt"
  ) => {
    if (!value || !selectedDataset) {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
            description={MESSAGE.MAP_VIEW_ACTION_COLUMN_FAILED}
          />
        ),
        autoClose: 2000
      });
      return;
    }

    let highlightValue: string | GeoJsonProperties = value;

    if (type === "grid") {
      const source = map?.getSource<GeoJSONSource>(selectedDataset);
      if (!source) return;

      const featureCollection = (await source.getData()) as FeatureCollection;
      const feature = featureCollection.features.find(
        (f) =>
          f.properties?.["Grid ID"] === value ||
          f.properties?.["H3 ID"] === value
      );

      if (!feature) return;
      highlightValue = feature;
    }

    handleHighlightDatasetFeatureByWkt({
      datasetId: selectedDataset,
      value: highlightValue,
      type
    });

    handleClose();
  };

  const getPreviewDataFromRow = (row: Row<SpatialAnalysisRow>) => {
    const { grid_id, h3_id } = row.original;

    const geometryValue = row
      .getAllCells()
      .find(
        (cell) =>
          (cell.column.columnDef as ExtendedColumnDef<SpatialAnalysisRow>)
            ?.type === DataSchemaType.GEOMETRY
      )
      ?.getValue() as string | undefined;

    const gridValue = grid_id ?? h3_id;
    const isGridType = !!gridValue && !geometryValue;

    const value = isGridType ? String(gridValue) : (geometryValue ?? null);
    const type: TypeGeomPreview = isGridType ? "grid" : "wkt";

    return { value, type };
  };

  return { handlePreviewClick, getPreviewDataFromRow };
};
